import type {
  DashboardAlerts,
  DashboardExtra,
  DashboardSummary,
  HuongTangTruong,
  SoNhapDoiSoat,
  TangTruong7Ngay,
  TheoTangTruong,
} from '@qlk/shared';
import { DASHBOARD_CACHE_SECONDS } from '@qlk/shared';
import { useQuery } from '@tanstack/react-query';

import { api, type LoiApi } from '../../lib/api.js';

const GOC = '/api/v1/dashboard';

export const KHOA = {
  tongQuan: (range: string) => ['dashboard', 'summary', range] as const,
  them: (range: string) => ['dashboard', 'them', range] as const,
  canhBao: ['dashboard', 'alerts'] as const,
  tangTruong: (huong: string, theo: string) =>
    ['dashboard', 'tang-truong-7-ngay', huong, theo] as const,
  /* Cùng khoá với `layout/badgeMenu`: badge cạnh mục menu và ô "Đối soát" của
     trang này hỏi đúng một câu, nên chúng phải dùng chung một cache. */
  soNhapDoiSoat: (period: string) => ['team', 'doi-soat', 'so-nhap', period] as const,
};

/** Máy chủ cache 5 phút nên phía trình duyệt giữ đúng bấy nhiêu, khỏi gọi lại thừa. */
const GIU_TUOI = DASHBOARD_CACHE_SECONDS * 1000;

export function useTongQuan(range: string) {
  return useQuery<DashboardSummary, LoiApi>({
    queryKey: KHOA.tongQuan(range),
    queryFn: () => api.get<DashboardSummary>(`${GOC}/summary?ky=${encodeURIComponent(range)}`),
    staleTime: GIU_TUOI,
  });
}

/**
 * Phần gộp thêm của trang Tổng quan: đếm kênh theo tình trạng kiếm tiền, theo
 * đường tiền, top 5 thực nhận và mốc đồng bộ gần nhất.
 *
 * Truy vấn **riêng** khỏi `/summary`: khối nào lỗi thì chỉ khối đó mất, và dải
 * chỉ số vẫn hiện được phần lấy từ `/summary`.
 */
export function useThem(range: string) {
  return useQuery<DashboardExtra, LoiApi>({
    queryKey: KHOA.them(range),
    queryFn: () => api.get<DashboardExtra>(`${GOC}/them?ky=${encodeURIComponent(range)}`),
    staleTime: GIU_TUOI,
  });
}

/**
 * Khung "Tăng trưởng lượt xem · 7 ngày".
 *
 * **Không** nhận kỳ của trang: khung luôn tính 7 ngày (xem
 * `components/KhungTangTruong`), nên khoá cache chỉ gồm hai nút chuyển. Đổi nút
 * là một khoá mới, nên bấm qua lại không gọi lại mạng.
 */
export function useTangTruong7Ngay(huong: HuongTangTruong, theo: TheoTangTruong) {
  return useQuery<TangTruong7Ngay, LoiApi>({
    queryKey: KHOA.tangTruong(huong, theo),
    queryFn: () =>
      api.get<TangTruong7Ngay>(
        `${GOC}/tang-truong-7-ngay?huong=${encodeURIComponent(huong)}&theo=${encodeURIComponent(theo)}`,
      ),
    staleTime: GIU_TUOI,
  });
}

export function useCanhBao() {
  return useQuery<DashboardAlerts, LoiApi>({
    queryKey: KHOA.canhBao,
    queryFn: () => api.get<DashboardAlerts>(`${GOC}/alerts`),
    staleTime: GIU_TUOI,
  });
}

/**
 * Còn bao nhiêu người chưa chốt đối soát của **tháng hiện tại** — ô "Đối soát"
 * trong hàng chỉ số vận hành.
 *
 * Gọi `GET /api/v1/team/doi-soat/so-nhap` (hợp đồng: `docs/modules/team.md`),
 * không đi qua hook của module 10: ma trận quyền cho mọi vai trò khác
 * `team: none`, nên chỉ `team:admin` mới bật được lượt gọi này — `bat` do trang
 * truyền vào sau khi đọc quyền, giống `layout/badgeMenu`.
 *
 * Endpoint **chỉ đếm**, không tính tiền của kênh nào, nên gọi nó ở trang chủ
 * không kéo theo một lượt tính doanh thu nào.
 */
export function useSoNhapDoiSoat(period: string, bat: boolean) {
  return useQuery<SoNhapDoiSoat, LoiApi>({
    queryKey: KHOA.soNhapDoiSoat(period),
    queryFn: () =>
      api.get<SoNhapDoiSoat>(`/api/v1/team/doi-soat/so-nhap?period=${encodeURIComponent(period)}`),
    enabled: bat && period !== '',
    staleTime: GIU_TUOI,
  });
}

export function duongDanXuatBaoCao(range: string): string {
  return `${GOC}/xuat-bao-cao?ky=${encodeURIComponent(range)}`;
}
